import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Tenant, Period, Notification, ApiError } from '@/types';
import { authApi, periodsApi } from '@/api/client';

// --- Auth Store --------------------------------------------------------------

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login({ email, password });
          localStorage.setItem('auth_token', response.access_token);
          if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token);
          }
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const apiError = err as ApiError;
          set({
            isLoading: false,
            error: apiError.detail || 'Login failed. Please check your credentials.',
            isAuthenticated: false,
            user: null,
            token: null,
          });
          throw err;
        }
      },

      logout: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
        window.location.href = '/login';
      },

      loadUser: async () => {
        const token = get().token || localStorage.getItem('auth_token');
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }
        try {
          set({ isLoading: true });
          const user = await authApi.getMe();
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          localStorage.removeItem('auth_token');
        }
      },

      refreshToken: async () => {
        try {
          const response = await authApi.refreshToken();
          localStorage.setItem('auth_token', response.access_token);
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
          });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

// --- App Store ---------------------------------------------------------------

interface AppState {
  currentTenant: Tenant | null;
  currentPeriod: Period | null;
  periods: Period[];
  periodsLoading: boolean;
  sidebarCollapsed: boolean;
  notifications: Notification[];

  setCurrentTenant: (tenant: Tenant | null) => void;
  setCurrentPeriod: (period: Period | null) => void;
  setPeriods: (periods: Period[]) => void;
  fetchPeriods: () => Promise<void>;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
}

export const useAppStore = create<AppState>()((set, get) => ({
  currentTenant: null,
  currentPeriod: null,
  periods: [],
  periodsLoading: false,
  sidebarCollapsed: false,
  notifications: [],

  setCurrentTenant: (tenant) => set({ currentTenant: tenant }),

  setCurrentPeriod: (period) => set({ currentPeriod: period }),

  setPeriods: (periods) => set({ periods }),

  fetchPeriods: async () => {
    set({ periodsLoading: true });
    try {
      const periods = await periodsApi.list();
      const currentPeriod = get().currentPeriod;
      // Auto-select the first open period if none is selected
      const openPeriods = periods.filter((p) => p.status === 'open');
      const selectedPeriod = currentPeriod
        ? periods.find((p) => p.id === currentPeriod.id) || openPeriods[0] || periods[0]
        : openPeriods[0] || periods[0];
      set({
        periods,
        currentPeriod: selectedPeriod || null,
        periodsLoading: false,
      });
    } catch {
      set({ periodsLoading: false });
    }
  },

  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
          read: false,
        },
        ...state.notifications,
      ].slice(0, 50),
    })),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  markNotificationRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      ),
    })),

  clearNotifications: () => set({ notifications: [] }),
}));
